
import { GoogleGenAI, Type } from "@google/genai";
import { Language } from "../types";

// Helper to init AI
const getAi = () => {
    const apiKey = process.env.API_KEY;
    if (!apiKey) {
        console.warn("API Key missing");
        return null;
    }
    return new GoogleGenAI({ apiKey: apiKey });
};

interface DetectedFood {
    name: string;
    quantity: number;
    unit: string;
}

export interface ParsedRecipe {
    name: string;
    ingredients: DetectedFood[];
    servings: number;
}

export async function analyzeFoodImage(imageBase64: string, language: Language): Promise<DetectedFood[]> {
    try {
        const ai = getAi();
        if (!ai) return [];

        const base64Data = imageBase64.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, '');

        const prompt = language === 'fr'
            ? `Identifie les aliments dans cette image. Pour chaque aliment, estime la quantité et l'unité.
               Format de réponse : JSON uniquement, une liste d'objets avec les clés "name", "quantity" (nombre), "unit".`
            : `Identify the food items in this image. For each item, estimate quantity and unit.
               Response Format: JSON only, list of objects with keys "name", "quantity" (number), "unit".`;

        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: {
                parts: [
                    { text: prompt },
                    {
                        inlineData: {
                            mimeType: 'image/jpeg',
                            data: base64Data
                        }
                    }
                ]
            },
            config: {
                responseMimeType: "application/json"
            }
        });

        const jsonText = response.text;
        if (!jsonText) return [];

        const result = JSON.parse(jsonText);
        return Array.isArray(result) ? result : [];

    } catch (error) {
        console.error("Image analysis failed", error);
        return [];
    }
}

export async function analyzeRecipeImage(imageBase64: string, language: Language): Promise<ParsedRecipe | null> {
    try {
        const ai = getAi();
        if (!ai) return null;

        const base64Data = imageBase64.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, '');

        const systemPrompt = language === 'fr'
            ? `Tu es un expert en lecture de livres de cuisine. Extrais les détails de la recette de cette image. 
               Règles critiques : 
               1. Trouve le nom de la recette. 
               2. Extrais TOUS les ingrédients sans exception, y compris le sel, le poivre et les épices si mentionnés.
               3. IMPORTANT: Pour le champ 'name' des ingrédients, n'écris QUE le nom de l'ingrédient de base. Retire TOUTES les instructions de préparation (ex: 'oignon émincé' devient 'oignon', 'pommes pelées' devient 'pomme').
               4. Trouve le nombre de portions (pour X personnes). Si non trouvé, mets 4 par défaut.
               5. Réponds UNIQUEMENT en JSON.`
            : `You are an expert at reading cookbooks. Extract the recipe details from this image.
               Critical Rules:
               1. Find the recipe name.
               2. Extract EVERY single ingredient listed.
               3. CRITICAL: For the ingredient 'name' field, output ONLY the base ingredient. Remove all preparation states like 'chopped', 'sliced', 'diced', 'peeled'. Example: 'Chopped Onions' -> Name: 'Onion'.
               4. Find the number of servings (serves X). If not found, default to 4.
               5. Respond ONLY in JSON.`;

        const responseSchema = {
            type: Type.OBJECT,
            properties: {
                name: { type: Type.STRING },
                servings: { type: Type.NUMBER, description: "Number of people the recipe is for. Default to 4 if not found." },
                ingredients: {
                    type: Type.ARRAY,
                    items: {
                        type: Type.OBJECT,
                        properties: {
                            name: { type: Type.STRING },
                            quantity: { type: Type.NUMBER },
                            unit: { type: Type.STRING }
                        },
                        required: ['name', 'quantity', 'unit']
                    }
                }
            },
            required: ['name', 'servings', 'ingredients']
        };

        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: {
                parts: [
                    { text: "Extract all ingredients meticulously. Keep names raw (e.g. 'Onion', not 'Sliced Onion')." },
                    {
                        inlineData: {
                            mimeType: 'image/jpeg',
                            data: base64Data
                        }
                    }
                ]
            },
            config: {
                systemInstruction: systemPrompt,
                responseMimeType: "application/json",
                responseSchema: responseSchema,
            }
        });

        const jsonText = response.text;
        if (!jsonText) return null;

        return JSON.parse(jsonText) as ParsedRecipe;

    } catch (error) {
        console.error("Recipe image analysis failed", error);
        return null;
    }
}
